import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../shared/utils/api';

const MAX_EXCHANGES = 5;

const LIMIT_MESSAGE =
  "I think we're going in circles on this one. Do a few more questions on the topic, then come back to me if you're still stuck. Sometimes seeing it applied to different facts helps more than me repeating the same explanation.";

function stripMarkdown(text) {
  return text
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[\s-]*-\s+/gm, '')
    .trim();
}

export default function AskAlexPanel({ questionId, wasCorrect, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const bottomRef = useRef(null);

  const exhausted = exchangeCount >= MAX_EXCHANGES;

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading || exhausted) return;
    const nextExchange = exchangeCount + 1;
    setMessages((prev) => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api('/api/questions/ask-alex', {
        method: 'POST',
        body: JSON.stringify({
          question_id: questionId,
          message: msg,
          exchange_number: nextExchange,
          was_correct: wasCorrect,
        }),
      });
      const cleanText = stripMarkdown(res.response || '');
      const isLast = res.exchanges_remaining === 0;
      setMessages((prev) => [
        ...prev,
        { role: 'alex', text: isLast ? LIMIT_MESSAGE : cleanText },
      ]);
      setExchangeCount(nextExchange);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'alex', text: 'Something went wrong. Try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-[#BFDBFE] rounded-r-lg overflow-hidden mb-sp-3">
      <div className="bg-blue-lightest px-sp-4 py-sp-3 flex items-center justify-between">
        <div className="flex items-center gap-sp-2 text-sm font-semibold text-accent">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          Ask Alex
        </div>
        <button
          onClick={onClose}
          className="bg-transparent border-0 text-text-muted cursor-pointer text-lg leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="p-sp-4 bg-bg-card">
        <div className="flex flex-col gap-sp-3 mb-sp-3">
          {messages.length === 0 && (
            <p className="text-sm text-text-muted">
              {wasCorrect
                ? 'Ask about the reasoning if anything was unclear.'
                : 'Ask a question about this problem. Alex can help you understand why.'}
            </p>
          )}
          {messages.map((m, i) => (
            <div key={i} className="flex gap-sp-3 items-start">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                  m.role === 'user'
                    ? 'bg-bg-surface text-text-secondary'
                    : 'bg-blue-lightest text-accent'
                }`}
              >
                {m.role === 'user' ? 'You' : 'AI'}
              </div>
              <div
                className={`rounded-r-md px-sp-3 py-sp-3 max-w-[80%] ${
                  m.role === 'user'
                    ? 'bg-bg-surface'
                    : 'bg-[#F8FAFC] border border-[#E2E8F0]'
                }`}
              >
                <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
                  {m.text}
                </p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-sp-3 items-start">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-blue-lightest text-accent">
                AI
              </div>
              <div className="rounded-r-md px-sp-3 py-sp-3 bg-[#F8FAFC] border border-[#E2E8F0]">
                <p className="text-sm text-text-muted">Thinking…</p>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        {!exhausted && (
          <div className="flex gap-sp-2">
            <input
              type="text"
              placeholder="Ask a follow-up question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              disabled={loading}
              className="flex-1 border border-border-soft rounded-r-md px-sp-3 py-sp-2 text-sm font-sans outline-none focus:border-accent bg-bg-card text-text-primary"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="px-sp-3 py-sp-2 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
